import { ChevronRight, Clock } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { PublicOffer } from "./publicOffer.types";
import { OfferTimeLeftLabel } from "./PublicOfferMedia";

type PublicOfferCardProps = {
  offer: PublicOffer;
  onOpenDetail: (offer: PublicOffer) => void;
  className?: string;
};

function OfferCardHeroImage({ offer }: { offer: PublicOffer }) {
  const [imageFailed, setImageFailed] = useState(false);
  const heroUrl = offer.detailImageUrl || offer.cardImageUrl;
  const alt = offer.imageAlt?.trim() || offer.title;
  const showPhoto = Boolean(heroUrl) && !imageFailed;

  return (
    <div className="relative aspect-[3/2] min-h-[200px] w-full overflow-hidden bg-slate-100 sm:min-h-[240px]">
      {showPhoto ? (
        <img
          src={heroUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setImageFailed(true)}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center bg-slate-100 text-sm text-slate-400"
          role="img"
          aria-label={alt}
        >
          No image
        </div>
      )}

      <svg
        className="absolute -bottom-px left-0 h-8 w-full text-white sm:h-10"
        viewBox="0 0 400 40"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          fill="currentColor"
          d="M0,40 C120,8 280,8 400,40 L400,40 L0,40 Z"
        />
      </svg>
    </div>
  );
}

function OfferCardMerchantLogo({ offer }: { offer: PublicOffer }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = offer.cardImageUrl;

  if (!logoUrl || logoFailed) return null;

  return (
    <div className="relative z-10 -mt-9 flex justify-center px-6">
      <div className="h-[7.75rem] overflow-hidden border border-slate-200 bg-white shadow-sm">
        <img
          src={logoUrl}
          alt={offer.merchantName || "Merchant"}
          loading="lazy"
          decoding="async"
          onError={() => setLogoFailed(true)}
          className="h-full w-full object-contain"
        />
      </div>
    </div>
  );
}

export function PublicOfferCard({
  offer,
  onOpenDetail,
  className,
}: PublicOfferCardProps) {
  const categoryLabel = (offer.categoryName || "Special Offer").toUpperCase();

  return (
    <article
      className={cn(
        "group flex h-full cursor-pointer flex-col overflow-hidden rounded-sm border border-slate-200 bg-white",
        "shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
        "transition-all duration-300 ease-out",
        "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)]",
        className,
      )}
      onClick={() => onOpenDetail(offer)}
    >
      <OfferCardHeroImage offer={offer} />
      <OfferCardMerchantLogo offer={offer} />

      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col bg-white px-6 pb-6 text-left",
          offer.cardImageUrl ? "pt-3" : "pt-5",
        )}
      >
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
          {categoryLabel}
        </p>

        <h2 className="mt-2 line-clamp-3 text-lg font-bold leading-snug text-slate-900">
          {offer.title}
        </h2>

        {offer.merchantName ? (
          <p className="mt-1.5 text-sm text-slate-400">
            By {offer.merchantName}
          </p>
        ) : null}

        <div
          className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5"
          role="status"
          aria-live="polite"
        >
          <Clock className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          <OfferTimeLeftLabel
            expiresAtMillis={offer.expiresAtMillis}
            variant="card"
          />
        </div>

        <div className="mt-5 flex items-center gap-1 text-sm font-semibold text-rose-900 transition-colors group-hover:text-rose-700">
          Read More
          <ChevronRight className="h-4 w-4" aria-hidden />
        </div>
      </div>
    </article>
  );
}
